import React, { useCallback, useEffect, useRef, useState } from 'react';

import { Button, Text, VStack } from '@squire/ui';

import { ResendIcon } from '@/components/_icons';
import { useScreenTranslations } from '@/hooks/useScreenTranslations';

import { styles } from './styles';
import type { ResendOtpButtonProps } from './types';

// Matches the server's per-phone resend cooldown, so mashing the button can't
// spam the throttle (it stays disabled with a countdown until this clears).
const COOLDOWN_SECONDS = 60;

const ResendOtpButton = ({
  onResend,
  disabled = false,
  error,
  onClearError,
}: ResendOtpButtonProps) => {
  const { t } = useScreenTranslations('shared');
  const [secondsLeft, setSecondsLeft] = useState(0);
  const intervalRef = useRef<ReturnType<typeof setInterval> | undefined>(
    undefined,
  );

  useEffect(() => () => clearInterval(intervalRef.current), []);

  const handleResend = useCallback(() => {
    onClearError?.();
    onResend();
    setSecondsLeft(COOLDOWN_SECONDS);
    clearInterval(intervalRef.current);
    intervalRef.current = setInterval(() => {
      setSecondsLeft((current) => {
        if (current <= 1) {
          clearInterval(intervalRef.current);
          return 0;
        }
        return current - 1;
      });
    }, 1000);
  }, [onResend, onClearError]);

  const isCoolingDown = secondsLeft > 0;
  const isDisabled = disabled || isCoolingDown;

  return (
    <VStack
      style={styles.container}
      accessibilityLabel={t('resendOtpButton.accessibility.resendSection')}
    >
      <Button
        onPress={handleResend}
        variant="secondary"
        style={styles.resendButton}
        disabled={isDisabled}
        accessibilityLabel={t('resendOtpButton.accessibility.resendButton')}
        accessibilityHint={t('resendOtpButton.accessibility.resendButtonHint')}
        aria-disabled={isDisabled || undefined}
        iconRight={<ResendIcon size={16} />}
      >
        {isCoolingDown
          ? t('resendOtpButton.resendIn', { seconds: secondsLeft })
          : t('resendOtpButton.resendCode')}
      </Button>
      {error && (
        <Text
          variant="s"
          color="textNegative"
          align="center"
          accessibilityRole="alert"
          accessibilityLiveRegion="polite"
        >
          {error}
        </Text>
      )}
      <Text
        variant="s"
        color="textSecondary"
        align="center"
        accessibilityRole="text"
      >
        {t('resendOtpButton.didNotReceiveCode')}
      </Text>
    </VStack>
  );
};

ResendOtpButton.displayName = 'ResendOtpButton';

export { ResendOtpButton };
